import { useState } from "react";
import { POSTER_SLOT_NAME, type PosterDoc, type PosterImageSource, type PosterStateResponse } from "@studio/shared";
import { InlineSpinner } from "../../components/InlineSpinner";
import { InlineError } from "../../components/StudioShell";
import { fetchPosterMedia, fetchPosterMediaWithPrint } from "../../poster/api";
import { loadPosterFonts } from "../../poster/fonts";
import type { PdfPaper } from "../../poster/exportPdf";
import { preloadImages, renderPosterPng } from "../../poster/render";

/**
 * 「포스터 내려받기」(목업 s3) — PDF A2(기본) · 60×90cm · A3 · A4 · PNG. 전부 학생 브라우저에서 만든다(서버 부담 0 · D5).
 * 🔑 기본은 A2 — 전시 포스터는 인쇄소에서 A2 로 뽑는다(2026-10-04 jery). A3·A4 는 그 아래에.
 * 🔑 60×90 은 A 계열보다 세로가 길다 — 폭에 맞춰 키우고 위아래 남는 자리를 배경색으로 채운다(2026-10-11 jery).
 * 🔑 Canva 로 자동으로 보내지 않는다. 내려받아 직접 올리도록 안내한다.
 */

type Choice = "a2" | "p60x90" | "a3" | "a4" | "png";

const PAPER: Record<Exclude<Choice, "png">, PdfPaper> = { a2: "A2", p60x90: "60x90", a3: "A3", a4: "A4" };
/** 파일 이름에 붙는 종이 이름 */
const PAPER_LABEL: Record<PdfPaper, string> = { A2: "A2", A3: "A3", A4: "A4", "60x90": "60×90cm" };
/** AI 그림을 인쇄용 사본(2배 업스케일)으로 받는 큰 종이 */
const PRINT_COPY: ReadonlySet<Choice> = new Set<Choice>(["a2", "p60x90"]);

const OPTIONS: Array<{ key: Choice; title: string; badge?: string; body: string }> = [
  { key: "a2", title: "PDF · A2", badge: "포스터 인쇄 · 이어서 고치기", body: "전시용 포스터 크기예요. 인쇄소에 맡길 때 써요. 글자가 글로 살아 있어 Canva에서 고칠 수 있어요." },
  { key: "p60x90", title: "PDF · 60×90cm", body: "인쇄소에서 60×90cm로 뽑을 때 써요. 포스터를 폭에 맞춰 키우고, 위아래에 남는 자리는 배경색으로 채워요." },
  { key: "a3", title: "PDF · A3", body: "같은 포스터를 A3로 줄여요. 큰 프린터용." },
  { key: "a4", title: "PDF · A4", body: "같은 포스터를 A4로 줄여요. 학교 프린터용." },
  { key: "png", title: "그림 파일 · PNG", body: "한 장짜리 그림이에요. 글자는 고칠 수 없어요. 그대로 붙이거나 보여 줄 때 써요." },
];

/**
 * @param print A2 · 60×90 PDF 만 — AI 그림은 인쇄용 사본(2배 업스케일)을 받는다. 사본이 아직이면 onPending 이 불린다.
 *              A3(약 150dpi)·A4·PNG·AR 인식 파일은 2K 로 충분해 원본을 쓴다.
 */
export function posterImageLoader(workId: string, state: Pick<PosterStateResponse, "cuts">, print?: { onPending: () => void }) {
  return async (source: PosterImageSource): Promise<Blob | null> => {
    const assetId = source.kind === "slot"
      ? state.cuts.find((cut) => cut.slot === source.slot)?.image_asset_id ?? null
      : source.asset_id;
    if (!assetId) return null;
    if (print && source.kind === "slot") {
      const fetched = await fetchPosterMediaWithPrint(workId, assetId, true);
      if (!fetched.printReady) print.onPending();
      return fetched.blob;
    }
    return fetchPosterMedia(workId, assetId);
  };
}

export function PosterDownloadDialog({
  workId,
  state,
  doc,
  onClose,
}: {
  workId: string;
  state: PosterStateResponse;
  doc: PosterDoc;
  onClose: () => void;
}) {
  const [choice, setChoice] = useState<Choice>("a2");
  const [busy, setBusy] = useState<"poster" | "images" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const download = async (): Promise<void> => {
    setBusy("poster");
    setError(null);
    setNotice(null);
    try {
      await loadPosterFonts();
      let printPending = false;
      const images = await preloadImages(doc, posterImageLoader(workId, state, PRINT_COPY.has(choice) ? { onPending: () => { printPending = true; } } : undefined));
      const { downloadBlob, posterFileName } = await import("../../poster/exportPdf");
      if (choice === "png") {
        downloadBlob(await renderPosterPng({ doc, qrUrl: state.ar_url, images }), posterFileName(state.student_name, ".png"));
      } else {
        const { buildPosterPdf } = await import("../../poster/exportPdf");
        const paper = PAPER[choice];
        const bytes = await buildPosterPdf([{ doc, qrUrl: state.ar_url, images }], paper, `${state.student_name}의 포스터`);
        downloadBlob(new Blob([bytes as BlobPart], { type: "application/pdf" }), posterFileName(state.student_name, ` (${PAPER_LABEL[paper]}).pdf`));
        if (printPending) setNotice("인쇄용 고화질 그림을 아직 만들고 있어서 이번 PDF 는 보통 화질로 만들었어요. 1~2분 뒤 다시 받으면 더 선명해요.");
      }
    } catch (caught) {
      console.error("[poster-download]", caught);
      setError("포스터를 만들지 못했어요. 잠깐 뒤에 다시 눌러 주세요.");
    } finally {
      setBusy(null);
    }
  };

  const downloadImages = async (): Promise<void> => {
    setBusy("images");
    setError(null);
    try {
      const { downloadBlob } = await import("../../poster/exportPdf");
      const names = POSTER_SLOT_NAME;
      for (const cut of state.cuts) {
        if (!cut.image_asset_id) continue;
        const blob = await fetchPosterMedia(workId, cut.image_asset_id);
        const extension = blob.type === "image/png" ? "png" : blob.type === "image/webp" ? "webp" : "jpg";
        downloadBlob(blob, `${state.student_name}의 ${names[cut.slot]}.${extension}`);
      }
    } catch {
      setError("그림을 받지 못했어요. 다시 눌러 주세요.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="exit-dialog-backdrop" onClick={(event) => { if (event.target === event.currentTarget && !busy) onClose(); }}>
      <div className="exit-dialog poster-download" role="dialog" aria-modal="true" aria-labelledby="poster-download-title">
        <button className="button-ghost exit-dialog-close" type="button" disabled={busy !== null} onClick={onClose}>
          <span aria-hidden="true">×</span>닫기
        </button>
        <h2 id="poster-download-title">포스터 내려받기</h2>
        <p>내려받아도 studio 의 포스터는 그대로 남아요. 계속 고칠 수 있어요.</p>
        <div className="poster-download-options" role="radiogroup" aria-label="파일 종류">
          {OPTIONS.map((option) => (
            <button
              key={option.key}
              type="button"
              role="radio"
              aria-checked={choice === option.key}
              className={`poster-download-option${choice === option.key ? " is-on" : ""}`}
              onClick={() => setChoice(option.key)}
            >
              <span className="poster-radio" aria-hidden="true" />
              <span>
                <b>{option.title}</b>
                {option.badge ? <em>{option.badge}</em> : null}
                <small>{option.body}</small>
              </span>
            </button>
          ))}
        </div>
        <div className="poster-canva">
          <b>더 다듬고 싶다면 Canva 를 써요</b>
          <ol>
            <li>PDF 를 내려받아요(A2 · A3 무엇이든)</li>
            <li>Canva 첫 화면에 파일을 끌어다 놓아요</li>
            <li>글자·그림을 눌러 이어서 고쳐요</li>
          </ol>
          <small>가까이 붙은 글상자는 Canva 에서 하나로 합쳐질 수 있어요. 겹치면 줄 간격을 조정하세요. 미리캔버스는 PDF 를 받지 않아요. 미리캔버스를 쓰려면 «AI 그림만 따로 받기» 로 그림을 받아 새로 꾸며요.</small>
        </div>
        {notice ? <p className="poster-download-notice" role="status">{notice}</p> : null}
        {error ? <InlineError>{error}</InlineError> : null}
        <div className="exit-dialog-actions">
          <button className="button-secondary" type="button" disabled={busy !== null} onClick={() => void downloadImages()}>
            {busy === "images" ? <InlineSpinner label="받는 중..." /> : "AI 그림만 따로 받기"}
          </button>
          <button className="button-primary" type="button" disabled={busy !== null} onClick={() => void download()}>
            {busy === "poster" ? <InlineSpinner label="만드는 중..." /> : "내려받기"}
          </button>
        </div>
      </div>
    </div>
  );
}
